"use client";

import {
 createContext,
 use,
 useState,
 useMemo,
 useCallback,
 type ReactNode,
} from "react";
import { type GeoLocation } from "@/lib/types/address";

export type HeroLocation = GeoLocation & { street?: string; line1?: string };

export type AutoLocationStatus =
 | "idle"
 | "requesting"
 | "granted"
 | "denied"
 | "error";

interface LocationContextValue {
 location: HeroLocation | null;
 setLocation: (location: HeroLocation | null) => void;
 autoLocationStatus: AutoLocationStatus;
 setAutoLocationStatus: (status: AutoLocationStatus) => void;
 clearLocation: () => void;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export function LocationProvider({
 children,
 initialLocation = null,
}: {
 children: ReactNode;
 initialLocation?: HeroLocation | null;
}) {
 const [location, setLocation] = useState<HeroLocation | null>(initialLocation);
 const [autoLocationStatus, setAutoLocationStatus] =
  useState<AutoLocationStatus>("idle");

 const clearLocation = useCallback(() => {
  setLocation(null);
  setAutoLocationStatus("idle");
 }, []);

 const value = useMemo(
  () => ({
   location,
   setLocation,
   autoLocationStatus,
   setAutoLocationStatus,
   clearLocation,
  }),
  [location, autoLocationStatus, clearLocation],
 );

 return (
  <LocationContext value={value}>
   {children}
  </LocationContext>
 );
}

const defaultLocationContext: LocationContextValue = {
 location: null,
 setLocation: () => {},
 autoLocationStatus: "idle",
 setAutoLocationStatus: () => {},
 clearLocation: () => {},
};

export function useLocationContext() {
 const context = use(LocationContext);
 return context ?? defaultLocationContext;
}

export { LocationContext };
